import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Search, List, BarChart2, Loader2, ChevronLeft, ChevronRight, X, Edit2, Check } from 'lucide-react';
import Button from '../../components/Button';
import Layout from '../../components/Layout';
import SimulationResultsContent from '../simulationResults/SimulationResultsContent';
import { debounce } from '../../utils/debounce';

const API_URL = import.meta.env.VITE_API_URL || '';

interface Audience {
  id: number;
  name: string;
}

interface Simulation {
  id: number;
  name: string;
  description: string;
  audience_id: number;
  audience_name: string;
  created_at: string;
  segment_count: number;
  persona_count: number;
  task?: string;
}

const AnalysisPage: React.FC = () => {
  const navigate = useNavigate();
  const { simulationId: urlSimId } = useParams<{ simulationId: string }>();
  
  // Audience and Simulation Data
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [selectedAudienceId, setSelectedAudienceId] = useState<number | null>(null);
  
  // UI state
  const [isAudienceDropdownOpen, setIsAudienceDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isListCollapsed, setIsListCollapsed] = useState(true); // Start with sidebar closed
  const [isSimulationsLoaded, setIsSimulationsLoaded] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const ITEMS_PER_PAGE = 15;
  
  // Search state
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  
  // Rename state
  const [editingSimulationId, setEditingSimulationId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  
  // Fetch audiences on component mount
  useEffect(() => {
    fetchAudiences();
  }, []);
  
  // Open sidebar with animation after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsListCollapsed(false); // Open sidebar with animation
    }, 100); // Small delay to ensure the initial render happens first
    
    return () => clearTimeout(timer);
  }, []); // Only run once on mount
  
  // Load simulations when audience changes
  useEffect(() => {
    fetchSimulations(selectedAudienceId, true); // Reset when audience changes
  }, [selectedAudienceId]);
  
  // Debounced search function
  const debouncedSearch = useMemo(
    () => debounce((searchValue: string) => {
      if (searchValue.trim()) {
        setIsSearchActive(true);
        setIsSearching(true);
        fetchSimulations(selectedAudienceId, true, searchValue.trim());
      } else if (isSearchActive) {
        // Clear search and reload normal paginated results
        setIsSearchActive(false);
        setIsSearching(true);
        fetchSimulations(selectedAudienceId, true);
      }
    }, 500),
    [selectedAudienceId, isSearchActive]
  );
  
  // Handle local search term changes
  useEffect(() => {
    setSearchTerm(localSearchTerm);
    debouncedSearch(localSearchTerm);
  }, [localSearchTerm, debouncedSearch]);
  
  // Handle immediate search action (for Enter key or button click)
  const handleImmediateSearch = useCallback(() => {
    if (localSearchTerm.trim()) {
      setSearchTerm(localSearchTerm);
      setIsSearchActive(true);
      setIsSearching(true);
      fetchSimulations(selectedAudienceId, true, localSearchTerm.trim());
    } else if (isSearchActive) {
      // Clear search and reload normal paginated results
      setIsSearchActive(false);
      setIsSearching(true);
      fetchSimulations(selectedAudienceId, true);
    }
  }, [localSearchTerm, selectedAudienceId, isSearchActive]);
  
  // Handle clear search
  const handleClearSearch = useCallback(() => {
    setLocalSearchTerm('');
    setSearchTerm('');
    setIsSearchActive(false);
    setIsSearching(true);
    fetchSimulations(selectedAudienceId, true);
  }, [selectedAudienceId]);
  
  // Handle rename simulation
  const handleStartRename = useCallback((simulation: Simulation, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation
    setEditingSimulationId(simulation.id);
    setEditingName(simulation.name);
  }, []);
  
  const handleCancelRename = useCallback(() => {
    setEditingSimulationId(null);
    setEditingName('');
  }, []);
  
  const handleSaveRename = useCallback(async (simulationId: number, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation(); // Prevent navigation
    }
    
    if (!editingName.trim()) {
      return;
    }
    
    setIsRenaming(true);
    
    try {
      const response = await fetch(`${API_URL}/simulations/${simulationId}/rename`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ name: editingName.trim() }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to rename simulation');
      }
      
      // Update the simulation in the list
      setSimulations(prev => 
        prev.map(sim => 
          sim.id === simulationId 
            ? { ...sim, name: editingName.trim() }
            : sim
        )
      );
      
      setEditingSimulationId(null);
      setEditingName('');
    } catch (err) {
      console.error('Error renaming simulation:', err);
      setError('Failed to rename simulation');
    } finally {
      setIsRenaming(false);
    }
  }, [editingName]);
  
  const handleRenameKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>, simulationId: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveRename(simulationId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelRename();
    }
  }, [handleSaveRename, handleCancelRename]);
  
  // Handle Enter key press in search input
  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleImmediateSearch();
    }
  }, [handleImmediateSearch]);
  
  const fetchAudiences = async () => {
    try {
      const response = await fetch(`${API_URL}/audience`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch audiences');
      }
      
      const data = await response.json();
      setAudiences(data.map((a: any) => ({ id: a.id, name: a.name })));
    } catch (err) {
      console.error('Error fetching audiences:', err);
    }
  };
  
  const fetchSimulations = async (audienceId?: number | null, reset = false, search = '') => {
    try {
      // If resetting, start from page 0
      const pageToLoad = reset ? 0 : currentPage;
      const start = pageToLoad * ITEMS_PER_PAGE;
      
      let url = `${API_URL}/simulations_paginated?start=${start}&limit=${ITEMS_PER_PAGE}`;
      if (audienceId) {
        url += `&audience_id=${audienceId}`;
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch simulations');
      }
      
      const data = await response.json();
      
      // Handle the paginated response
      if (reset) {
        setSimulations(data.simulations);
        setCurrentPage(0);
      } else {
        setSimulations(prev => [...prev, ...data.simulations]);
      }
      
      setHasMore(data.pagination.has_more);
      setLoading(false);
      setIsLoadingMore(false);
      setIsSimulationsLoaded(true);
      setIsSearching(false); // Clear searching state
    } catch (err) {
      console.error('Error fetching simulations:', err);
      setLoading(false);
      setIsLoadingMore(false);
      setIsSearching(false); // Clear searching state on error
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  // Load more simulations for infinite scroll
  const loadMoreSimulations = async () => {
    if (isLoadingMore || !hasMore) return;
    
    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    
    const start = nextPage * ITEMS_PER_PAGE;
    let url = `${API_URL}/simulations_paginated?start=${start}&limit=${ITEMS_PER_PAGE}`;
    if (selectedAudienceId) {
      url += `&audience_id=${selectedAudienceId}`;
    }
    
    try {
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch more simulations');
      }
      
      const data = await response.json();
      setSimulations(prev => [...prev, ...data.simulations]);
      setHasMore(data.pagination.has_more);
      setIsLoadingMore(false);
    } catch (err) {
      console.error('Error loading more simulations:', err);
      setIsLoadingMore(false);
    }
  };
  
  // Handle scroll for infinite loading
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const threshold = 100; // pixels from bottom to trigger load
    
    if (element.scrollHeight - element.scrollTop - element.clientHeight < threshold) {
      if (hasMore && !isLoadingMore && !isSearchActive) {
        // Only load more if not searching
        loadMoreSimulations();
      }
    }
  };
  
  // Don't filter client-side anymore - server handles search
  const filteredSimulations = simulations;
  
  const renderSimulationsList = () => (
    <div className="flex flex-col h-full p-4 overflow-hidden">
      {/* This container holds all content that will slide out */}
      <div
        className={`transition-all duration-500 ease-in-out flex flex-col h-full min-w-[250px] ${
          isListCollapsed ? 'opacity-0 -translate-x-full' : 'opacity-100 translate-x-0'
        }`}
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-grow">
            <button
              type="button"
              onClick={() => setIsAudienceDropdownOpen(!isAudienceDropdownOpen)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
            >
              <span>{selectedAudienceId ? 
                audiences.find(a => a.id === selectedAudienceId)?.name : 
                'All Audiences'}</span>
              <ChevronDown className="h-4 w-4" />
            </button>
            
            {isAudienceDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg max-h-56 overflow-auto py-1 border border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedAudienceId(null);
                    setIsAudienceDropdownOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                >
                  All Audiences
                </button>
                {audiences.map((audience) => (
                  <button
                    type="button"
                    key={audience.id}
                    onClick={() => {
                      setSelectedAudienceId(audience.id);
                      setIsAudienceDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    {audience.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="relative">
          <input
            type="text"
            placeholder="Search simulations..."
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all placeholder-gray-400"
          />
          <div className="absolute right-1 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
            {localSearchTerm && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="p-1.5 rounded-md hover:bg-gray-100 active:bg-gray-200 transition-all group"
                title="Clear search"
              >
                <X className="h-4 w-4 text-gray-500 group-hover:text-gray-700 transition-colors" />
              </button>
            )}
            <button
              type="button"
              onClick={handleImmediateSearch}
              className="p-1.5 rounded-md hover:bg-primary/10 active:bg-primary/20 transition-all group"
              title="Press Enter or click to search"
              disabled={isSearching}
            >
              {isSearching ? (
                <Loader2 className="h-4 w-4 text-primary animate-spin" />
              ) : (
                <Search className="h-4 w-4 text-gray-500 group-hover:text-primary transition-colors" />
              )}
            </button>
          </div>
        </div>
        
        <div 
          className={`flex-1 mt-4 -mx-2 px-2 relative ${isSearching ? 'overflow-hidden' : 'overflow-y-auto'}`}
          onScroll={handleScroll}
        >
          {/* Search loading overlay */}
          {isSearching && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center animate-fade-in">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
              <p className="text-sm font-medium text-gray-700">Searching simulations...</p>
              <p className="text-xs text-gray-500 mt-1">Please wait</p>
            </div>
          )}
          
          {loading && !isSearching ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500 mr-2" />
              <span className="text-sm text-gray-600">Loading simulations...</span>
            </div>
          ) : filteredSimulations.length === 0 && !isSearching ? (
            <div className="text-center text-gray-500 py-8">
              <List className="h-12 w-12 mx-auto opacity-30 mb-3" />
              <p>No simulations found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredSimulations.map((simulation) => (
                <button
                  type="button"
                  key={simulation.id}
                  onClick={() => navigate(`/analysis/${simulation.id}`)}
                  className={`w-full text-left p-3 rounded-lg hover:bg-blue-50 transition-colors border group relative ${
                    urlSimId === simulation.id.toString() 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200'
                  }`}
                >
                  {editingSimulationId === simulation.id ? (
                    <div className="flex items-center gap-2 mb-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => handleRenameKeyDown(e, simulation.id)}
                        className="flex-1 px-2 py-1 text-sm border border-primary rounded focus:outline-none focus:ring-2 focus:ring-primary font-medium text-blue-600"
                        autoFocus
                        disabled={isRenaming}
                      />
                      <button
                        type="button"
                        onClick={(e) => handleSaveRename(simulation.id, e)}
                        disabled={isRenaming || !editingName.trim()}
                        className="p-1 rounded hover:bg-green-100 text-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Save (Enter)"
                      >
                        {isRenaming ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelRename();
                        }}
                        disabled={isRenaming}
                        className="p-1 rounded hover:bg-red-100 text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Cancel (Esc)"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-medium text-blue-600 flex-1">{simulation.name}</div>
                      <button
                        type="button"
                        onClick={(e) => handleStartRename(simulation, e)}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-blue-100 text-blue-600 transition-opacity ml-2"
                        title="Rename simulation"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                  <div className="text-sm text-gray-600 mt-1">
                    {simulation.description?.substring(0, 60) || 'No description'}
                    {(simulation.description?.length || 0) > 60 ? '...' : ''}
                  </div>
                  <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                    {!['concept-test', 'driving-concept-test', 'vitamin-concept-test'].includes(simulation.task || '') &&
                     simulation.audience_name !== 'Driving Simulations' && (
                      <span>{simulation.audience_name}</span>
                    )}
                    <span>{formatDate(simulation.created_at)}</span>
                  </div>
                </button>
              ))}
              
              {/* Loading more indicator */}
              {isLoadingMore && (
                <div className="flex justify-center items-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-500 mr-2" />
                  <span className="text-sm text-gray-600">Loading more simulations...</span>
                </div>
              )}
              
              {/* No more items indicator */}
              {!hasMore && simulations.length > 0 && !isSearchActive && (
                <div className="text-center py-4 text-sm text-gray-500">
                  No more simulations to load
                </div>
              )}
              
              {/* Search active indicator */}
              {isSearchActive && simulations.length > 0 && (
                <div className="text-center py-4 text-sm text-gray-500">
                  Showing search results for "{searchTerm}"
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
  
  const renderNoSimulationSelectedState = () => (
    <div className="flex flex-col items-center justify-center h-full text-gray-500">
      <BarChart2 className="h-16 w-16 mb-4 opacity-30" />
      <p className="text-xl font-medium mb-2">Select a simulation</p>
      <p>Choose a simulation from the list to view its analysis</p>
    </div>
  );

  const renderLoadingAnimation = () => (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="animate-spin mb-6">
        <Loader2 className="h-12 w-12 text-blue-500" />
      </div>
      <h3 className="text-xl font-semibold mb-2">Loading...</h3>
    </div>
  );
  
  const handleSimulationError = (errorMsg: string) => {
    setError(errorMsg);
  };

  return (
    <Layout>
      <div className="flex justify-between mb-6 items-center back-button-container no-print">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/')}
          icon={<ArrowLeft className="w-4 h-4 mr-1" />}
        >
          Back to Home
        </Button>
        <h1 className="text-xl font-semibold text-gray-800">Analysis Dashboard</h1>
      </div>
      
      {error && !urlSimId && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}
      <div className="flex flex-row items-start h-[calc(100vh-160px)] analysis-page-container">
        <div
          className="relative h-full transition-[width] duration-700 ease-in-out flex-shrink-0 simulation-sidebar"
           style={{ width: isListCollapsed ? '5.5rem' : 'clamp(300px, 25%, 400px)' }}
        >
          <div className="absolute top-4 right-4 z-10">
            <button
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={() => setIsListCollapsed((prev) => !prev)}
              title={isListCollapsed ? 'Show Simulation List' : 'Collapse Simulation List'}
            >
              {isListCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            </button>
          </div>
          <div
            className={`h-full rounded-xl overflow-hidden transition-colors duration-700 ease-in-out ${
              isListCollapsed ? 'bg-transparent shadow-none' : 'bg-white shadow-sm'
            }`}
          >
            {renderSimulationsList()}
          </div>
        </div>
        <div className="flex-1 bg-white rounded-xl shadow-sm overflow-hidden h-full ml-4 simulation-content-wrapper">
          {urlSimId ? (
            <SimulationResultsContent
              simulationId={urlSimId}
              onError={handleSimulationError}
            />
          ) : (
            renderNoSimulationSelectedState()
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AnalysisPage;